import { SquarePen } from "lucide-react";
import { useNavigate } from "react-router-dom";
import ChronometerIcon from "../../../assets/icons/RecentCourseIcon.svg";
import { useBreadcrumb } from "../../../context/BreadcrumbContext";
import { useEffect, useState, useContext, useCallback } from "react";
import TopicService from "../../../services/TopicService";
import AuthContext from "../../../auth/AuthContext";

const TopicCard = () => {
  const navigate = useNavigate();
  const { setDynamicLabels } = useBreadcrumb();
  const authContext = useContext(AuthContext);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState({});

    useEffect(() => {
    const topicAccess = authContext.getPermissions('knowledgemanagement');
    if (topicAccess.filter((f) => f.access.includes(1)).length === 0) {
      navigate('/');
    }
    setPermissions({
      showAdd: topicAccess.some((f) => f.access.includes(2)),
      showEdit: topicAccess.some((f) => f.access.includes(3)),
      showDelete: topicAccess.some((f) => f.access.includes(4)),
    });
  }, [authContext, navigate]);

  useEffect(() => {
    setDynamicLabels({});
  }, [setDynamicLabels]);


  const fetchTopics = useCallback(async () => {
    try {
      setLoading(true);

      const response = await TopicService.GetAllTopics(authContext);

      const subTopicsResponse = await TopicService.GetAllSubTopics(authContext);
      const allSubTopics = subTopicsResponse?.data || [];

      if (response?.data) {
        const filteredTopics = response.data.filter((t) => !t.isDeleted);

        const topicsWithDetails = filteredTopics.map((topic) => {
          const topicSubTopics = allSubTopics.filter(
            (st) => st.topicId === topic.id && !st.isDeleted,
          );

          let totalVideos = 0;
          for (const subTopic of topicSubTopics) {
            if (subTopic.videos && Array.isArray(subTopic.videos)) {
              totalVideos += subTopic.videos.length;
            }
          }

          return {
            topicId: topic.id,
            topicTitle: topic.name,
            description: topic.description || "No description available",
            videos: totalVideos,
            assessment: topic.hasAssessment ? 1 : 0,
            completed: 0,
            total: totalVideos,
            progressPercent: 0,
          };
        });

        setTopics(topicsWithDetails);
      }
    } catch (error) {
      console.error("Error fetching topics:", error);
    } finally {
      setLoading(false);
    }
  }, [authContext]);

    useEffect(() => {
    fetchTopics();
  }, [fetchTopics]);

if (loading) {
  return (
    <>
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={i}
          className="
            bg-white dark:bg-slate-800
            border border-gray-200 dark:border-slate-700
            rounded-xl shadow-sm
            p-4
            flex flex-col
            h-full
            animate-pulse
          "
        >
          {/* Skeleton Content */}
          <div className="flex justify-between items-start mb-3">
            <div className="flex items-center gap-3">
              <div className="w-14 h-12 rounded bg-slate-200 dark:bg-slate-700" />
              <div className="h-4 w-36 rounded bg-slate-200 dark:bg-slate-700" />
            </div>
            <div className="h-4 w-4 rounded bg-slate-200 dark:bg-slate-700" />
          </div>

          <div className="space-y-2 mb-4">
            <div className="h-3 w-full rounded bg-slate-200 dark:bg-slate-700" />
            <div className="h-3 w-11/12 rounded bg-slate-200 dark:bg-slate-700" />
            <div className="h-3 w-9/12 rounded bg-slate-200 dark:bg-slate-700" />
          </div>

          <div className="mt-auto">
            <div className="flex justify-between mb-4">
              <div className="h-3 w-20 rounded bg-slate-200 dark:bg-slate-700" />
              <div className="h-3 w-24 rounded bg-slate-200 dark:bg-slate-700" />
            </div>

            <hr className="border-gray-200 dark:border-slate-700 mb-4" />

            <div className="flex items-center gap-3">
              <div className="h-3 w-16 rounded bg-slate-200 dark:bg-slate-700" />
              <div className="flex-1 h-2 rounded-full bg-slate-200 dark:bg-slate-700" />
              <div className="h-3 w-10 rounded bg-slate-200 dark:bg-slate-700" />
            </div>
          </div>
        </div>
      ))}
    </>
  );
}


  return (
    <>
      {topics.map((topic) => (
        <div
          key={topic.topicId}
          onClick={() => {
            setDynamicLabels({
              topicId: topic.topicId,
              topicTitle: topic.topicTitle,
            });

            navigate(`/knowledge-management/topics/${topic.topicId}`);
          }}
          className="
            bg-white dark:bg-slate-800
            border border-gray-200 dark:border-slate-700
            rounded-xl shadow
            p-4
            hover:shadow-md
            transition
            cursor-pointer
            flex flex-col
            h-full
          "
        >
          {/* Header */}
          <div className="flex justify-between items-start mb-3">
            <div className="flex items-center gap-3">
              <img src={ChronometerIcon} alt="Topic" className="w-14 h-12" />
              <h6 className="font-medium text-black dark:text-slate-100 text-base">
                {topic.topicTitle}
              </h6>
            </div>
{permissions.showEdit && (
            <SquarePen
              size={18}
              strokeWidth={2.2}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/knowledge-management/topics/${topic.topicId}/edit`);
              }}
              className="text-gray-400 hover:text-blue-600 cursor-pointer"
            />
     )}
          </div>

          {/* Description */}
          <div
            className="text-sm text-gray-800 dark:text-slate-400 mb-4"
            dangerouslySetInnerHTML={{ __html: topic.description }}
          />

          {/* Videos & Assessment */}
          <div className="mt-auto">
            <div className="flex justify-between text-sm text-gray-700 dark:text-slate-300 mb-4">
              <div>
                <span className="font-medium">Videos:</span> {topic.videos}
              </div>
              <div>
                <span className="font-medium">Assessment:</span>{" "}
                {topic.assessment}
              </div>
            </div>

            {/* Progress */}
            <hr className="border-gray-200 dark:border-slate-700 mb-4" />

            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600 dark:text-slate-400">
                Progress
              </span>

              <div className="flex-1 h-2 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                <div
                  className="
                    h-full bg-gradient-to-r
                    from-teal-500 to-blue-600
                    dark:from-cyan-400 dark:to-indigo-500
                  "
                  style={{
                    width: `${topic.progressPercent}%`,
                  }}
                />
              </div>

              <span className="text-sm text-gray-600 dark:text-slate-400">
                {topic.completed}/{topic.total}
              </span>
            </div>
          </div>
        </div>
      ))}
    </>
  );
};

export default TopicCard;
